import React, { useEffect, useMemo, useState } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebaseConfig";
import {
  addHeroSlide,
  listHeroSlidesAdmin,
  updateHeroSlide,
  deleteHeroSlide,
} from "../services/heroService";

export default function HeroEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [posterFile, setPosterFile] = useState(null);
  const [form, setForm] = useState({
    type: "image",
    src: "",
    poster: "",
    alt: "",
    title: "",
    subtitle: "",
    ctaText: "",
    ctaHref: "",
    interval: 5000,
    active: true,
    order: 0,
  });
  const [editing, setEditing] = useState(null);

  const isVideo = useMemo(() => form.type === "video", [form.type]);

  const load = async () => {
    setLoading(true);
    const list = await listHeroSlidesAdmin();
    setItems(list);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const uploadIfNeeded = async () => {
    let src = form.src;
    let poster = form.poster;
    if (file) {
      const path = `hero/${Date.now()}-${file.name}`;
      const r = ref(storage, path);
      await uploadBytes(r, file);
      src = await getDownloadURL(r);
    }
    if (posterFile) {
      const path = `hero/${Date.now()}-poster-${posterFile.name}`;
      const r = ref(storage, path);
      await uploadBytes(r, posterFile);
      poster = await getDownloadURL(r);
    }
    return { src, poster };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { src, poster } = await uploadIfNeeded();
      const payload = {
        ...form,
        src,
        poster,
        interval: Number(form.interval || 5000),
        order: Number(form.order || Date.now()),
      };
      if (editing) {
        await updateHeroSlide(editing.id, payload);
      } else {
        await addHeroSlide(payload);
      }
      setForm({
        type: "image",
        src: "",
        poster: "",
        alt: "",
        title: "",
        subtitle: "",
        ctaText: "",
        ctaHref: "",
        interval: 5000,
        active: true,
        order: 0,
      });
      setFile(null);
      setPosterFile(null);
      setEditing(null);
      await load();
    } catch (e) {
      alert(e?.message || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (it) => {
    setEditing(it);
    setForm({
      type: it.type,
      src: it.src,
      poster: it.poster || "",
      alt: it.alt || "",
      title: it.title || "",
      subtitle: it.subtitle || "",
      ctaText: it.ctaText || "",
      ctaHref: it.ctaHref || "",
      interval: it.interval || 5000,
      active: !!it.active,
      order: it.order || 0,
    });
    setFile(null);
    setPosterFile(null);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette bannière ?")) return;
    await deleteHeroSlide(id);
    await load();
  };

  const move = async (idx, dir) => {
    const a = items[idx];
    const b = items[idx + dir];
    if (!a || !b) return;
    await Promise.all([
      updateHeroSlide(a.id, { order: b.order }),
      updateHeroSlide(b.id, { order: a.order }),
    ]);
    await load();
  };

  const detectType = (f) => (f?.type?.startsWith("video/") ? "video" : "image");

  return (
    <div className="container py-4">
      <div className="row g-4">
        <div className="col-lg-5">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="mb-3">
                {editing ? "Modifier une bannière" : "Ajouter une bannière"}
              </h5>
              <form onSubmit={onSubmit}>
                <div className="mb-3">
                  <label className="form-label">Fichier image/vidéo</label>
                  <input
                    type="file"
                    accept="image/*,video/*"
                    className="form-control"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setFile(f);
                      if (f) setForm((s) => ({ ...s, type: detectType(f) }));
                    }}
                  />
                  {file && (
                    <div className="small text-muted mt-1">
                      {file.name} — {file.type}
                    </div>
                  )}
                </div>

                {isVideo && (
                  <div className="mb-3">
                    <label className="form-label">Poster (image)</label>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={(e) =>
                        setPosterFile(e.target.files?.[0] || null)
                      }
                    />
                  </div>
                )}

                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label">Type</label>
                    <select
                      className="form-select"
                      value={form.type}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, type: e.target.value }))
                      }
                    >
                      <option value="image">Image</option>
                      <option value="video">Vidéo</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Intervalle (ms)</label>
                    <input
                      className="form-control"
                      type="number"
                      value={form.interval}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          interval: Number(e.target.value || 0),
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="mb-2 mt-2">
                  <label className="form-label">Titre</label>
                  <input
                    className="form-control"
                    value={form.title}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, title: e.target.value }))
                    }
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Sous-titre</label>
                  <input
                    className="form-control"
                    value={form.subtitle}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, subtitle: e.target.value }))
                    }
                  />
                </div>
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label">CTA texte</label>
                    <input
                      className="form-control"
                      value={form.ctaText}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, ctaText: e.target.value }))
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">CTA lien</label>
                    <input
                      className="form-control"
                      value={form.ctaHref}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, ctaHref: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div className="row g-2 mt-2">
                  <div className="col-md-6">
                    <label className="form-label">Actif</label>
                    <select
                      className="form-select"
                      value={String(form.active)}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, active: e.target.value === "true" }))
                      }
                    >
                      <option value="true">Oui</option>
                      <option value="false">Non</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Ordre</label>
                    <input
                      className="form-control"
                      type="number"
                      value={form.order}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          order: Number(e.target.value || 0),
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="d-grid mt-3">
                  <button className="btn btn-success" disabled={saving}>
                    {saving ? "Enregistrement..." : editing ? "Mettre à jour" : "Ajouter"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="m-0">Bannières</h5>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={load}
                  disabled={loading}
                >
                  Rafraîchir
                </button>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status" />
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle table-hover">
                    <thead>
                      <tr>
                        <th style={{ width: 120 }}>Aperçu</th>
                        <th>Titre</th>
                        <th>Type</th>
                        <th>Actif</th>
                        <th>Ordre</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, idx) => (
                        <tr key={it.id}>
                          <td>
                            {it.type === "image" ? (
                              it.src ? (
                                <img
                                  src={it.src}
                                  alt={it.alt || "preview"}
                                  style={{
                                    width: 110,
                                    height: 60,
                                    objectFit: "cover",
                                    borderRadius: 6,
                                  }}
                                />
                              ) : (
                                "—"
                              )
                            ) : it.poster ? (
                              <img
                                src={it.poster}
                                alt="poster"
                                style={{
                                  width: 110,
                                  height: 60,
                                  objectFit: "cover",
                                  borderRadius: 6,
                                }}
                              />
                            ) : (
                              <span className="text-muted small">vidéo</span>
                            )}
                          </td>
                          <td className="fw-semibold">{it.title || "—"}</td>
                          <td className="text-muted small">{it.type}</td>
                          <td>
                            {it.active ? (
                              <span className="badge text-bg-success">oui</span>
                            ) : (
                              <span className="badge text-bg-secondary">non</span>
                            )}
                          </td>
                          <td>{it.order}</td>
                          <td className="text-end">
                            <div className="btn-group">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => move(idx, -1)}
                                disabled={idx === 0}
                              >
                                ↑
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => move(idx, +1)}
                                disabled={idx === items.length - 1}
                              >
                                ↓
                              </button>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => onEdit(it)}
                              >
                                Éditer
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => onDelete(it.id)}
                              >
                                Supprimer
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {items.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-muted small">
                            Aucune bannière pour l’instant.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
